import { Inject, Injectable } from "@angular/core";

import DefaultConfig from "../config/config.json";
import { IConfig } from "../interfaces/config";
import { LogService } from "./log.service";
import { IAnalyzerConfig } from "../interfaces/analyzer-config";
import { StringUtils } from "../utils/string.utils";
import { EvFlow, IAttachmentIndex } from "../models/shared.model";
import { SettingResponse } from "../models/settings.model";

@Injectable()
export class ConfigService {
  private currentConfig: IConfig;

  private getDefaultConfiguration(): IConfig {
    return { ...DefaultConfig };
  }

  constructor(
      @Inject(LogService) private log: LogService
  ) {}

  getConfiguration(): IConfig {
    const config: IConfig = this.currentConfig ? this.currentConfig : this.getDefaultConfiguration();

    config.Analyzers.forEach((analyzer) => {
      if (analyzer.serializableFields) {
        analyzer.serializableFields.forEach((key) => {
          const setting: any = this.getSetting(analyzer.name, key);

          if (setting) {
            analyzer[key] = setting;
          }
        });
      }
    });

    return config;
  }

  getFlowPointsAndConditions(): EvFlow {
    return {
      EvaluationFlows: this.getConfiguration()?.EvaluationFlows,
      multipleConditions: this.getConfiguration()?.multipleConditions
    };
  }

  getConfigurationValue(property: string): any {
    return this.getDefaultConfiguration()[property];
  }

  getAnalyzerConfiguration(name: string): IAnalyzerConfig {
    const config: IConfig = this.getConfiguration();
    return config.Analyzers.find((a) => a.name === name)!;
  }

  getSettingsConfiguration(): SettingResponse {
    return this.getConfiguration()?.Settings;
  }

  getAttachmentIndexConfiguration(type: string): IAttachmentIndex {
    const config: IConfig = this.getConfiguration();
    return config.AttachmentsIndex.find((a) => a.type === type)!;
  }

  async saveConfiguration(config: IConfig): Promise<void> {
    return new Promise<void>((resolve) => {
      config.Analyzers.forEach((analyzer) => {
        if (analyzer.serializableFields) {
          analyzer.serializableFields.forEach((key) => {
            this.setSetting(analyzer.name, key, analyzer[key]);
          });
        }
      });

      Office.context.roamingSettings.saveAsync((result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(result.error);
          this.log.logException(new Error(`Error saving settings - ${StringUtils.formatOfficeError(result.error)}`));
        }

        this.currentConfig = config;
        resolve();
      });
    });
  }

  private getSetting(type: string, key: string): any {
    let setting: any = Office.context.roamingSettings.get(`${type}:${key}`);
    return setting ? setting : null;
  }

  private setSetting(type: string, key: string, value: any): void {
    Office.context.roamingSettings.set(`${type}:${key}`, value);
  }
}
